import React, { useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Box,
  Typography,
  LinearProgress,
  Chip,
  Link,
  Snackbar,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Grid,
  FormHelperText,
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import api from '../services/api';
import { fetchCurrentUser } from '../services/auth';
import { listSites } from '../services/sites';
import { updateUsername } from '../services/employee';
import {
  getCustomFieldsMetadata,
  updateCustomFieldsMetadata,
  getEmployeeCustomFields,
  updateEmployeeCustomFields,
} from '../services/customFields';

const EmployeesPage = () => {
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [professions, setProfessions] = useState([]);
  const [sites, setSites] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({}); // Track field errors for red borders
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    profession_id: '',
    cin: '',
    date_naissance: '',
    lieu_naissance: '',
    date_embauche: '',
    poste: '',
    date_sortie: '',
    adresse: '',
    numero_securite_sociale: '',
    genre: '',
    profile_picture: '',
    site_id: '',
    manager_id: '',
    phone: '',
    email: '',
    situation_familiale: '',
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Custom fields state
  const [metaOpen, setMetaOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [metadata, setMetadata] = useState({});
  const [metaDraft, setMetaDraft] = useState({});
  const [fieldsDraft, setFieldsDraft] = useState({});
  const [fieldsEmployee, setFieldsEmployee] = useState(null);
  const [fieldsSaving, setFieldsSaving] = useState(false);
  // Leave balance editor (admin)
  const [lbOpen, setLbOpen] = useState(false);
  const [lbEmployee, setLbEmployee] = useState(null);
  const [lbValue, setLbValue] = useState('');
  const [lbSaving, setLbSaving] = useState(false);
  // Auth
  const [userRole, setUserRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // UX helpers
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Filters
  const [filterProfessionId, setFilterProfessionId] = useState('');
  const [filterSiteId, setFilterSiteId] = useState('');
  const [filterTrialStatus, setFilterTrialStatus] = useState('all'); // all | in_trial | ended | none
  const [filterHireFrom, setFilterHireFrom] = useState(''); // yyyy-mm-dd
  const [filterHireTo, setFilterHireTo] = useState(''); // yyyy-mm-dd
  const [filterSituationFamiliale, setFilterSituationFamiliale] = useState(''); // situation familiale filter
  const [filterTypeContrat, setFilterTypeContrat] = useState(''); // type contrat filter
  const [filtersOpen, setFiltersOpen] = useState(true);
  // Details modal state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);

  // Metadata add-inline form
  const [newMetaLabel, setNewMetaLabel] = useState('');
  const [newMetaType, setNewMetaType] = useState('text');
  const [newMetaEditableBy, setNewMetaEditableBy] = useState('admin');
  const [metaSaving, setMetaSaving] = useState(false);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoadingEmployees(true);
        const response = await api.get('/employees');
        const transformedEmployees = response.data.map(employee => ({
          ...employee,
          profession: employee.profession ? employee.profession.name : '............................',
          site: employee.site ? employee.site.name : '—',
          manager: employee.manager ? `${employee.manager.first_name || ''} ${employee.manager.last_name || ''}`.trim() : '—',
        }));
        setEmployees(transformedEmployees);
      } catch (error) {
        console.error('Erreur lors du chargement des employés:', error);
        setSnack({ open: true, message: "Erreur lors du chargement des employés", severity: 'error' });
      } finally {
        setLoadingEmployees(false);
      }
    };
    fetchEmployees();
  }, []);

  // Load current user role to gate admin-only actions
  useEffect(() => {
    const loadUser = async () => {
      const me = await fetchCurrentUser();
      if (me && (me.role !== undefined && me.role !== null)) {
        setUserRole(me.role);
      }
    };
    loadUser();
  }, []);

  // Debounce search input for smoother UX
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const fetchProfessions = async () => {
      try {
        const response = await api.get('/professions');
        // Trier les professions par nom
        const sortedProfessions = response.data.sort((a, b) => a.name.localeCompare(b.name));
        setProfessions(sortedProfessions);
      } catch (error) {
        console.error('Erreur lors du chargement des professions:', error);
      }
    };
    fetchProfessions();
  }, []);

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const res = await listSites();
        // Trier les sites par nom
        const sortedSites = (res.data || []).sort((a, b) => a.name.localeCompare(b.name));
        setSites(sortedSites);
      } catch (e) {
        console.error('Erreur lors du chargement des sites:', e);
      }
    };
    fetchSites();
  }, []);

  const handleOpenModal = (employee = null) => {
    // Clear field errors when opening modal
    setFieldErrors({});
    
    if (employee) {
      setFormData({
        first_name: employee.first_name || '',
        last_name: employee.last_name || '',
        profession_id: employee.profession_id || '',
        cin: employee.cin || '',
        date_naissance: employee.date_naissance ? employee.date_naissance.split('T')[0] : '',
        lieu_naissance: employee.lieu_naissance || '',
        date_embauche: employee.date_embauche ? employee.date_embauche.split('T')[0] : '',
        poste: employee.poste || '',
        date_sortie: employee.date_sortie ? employee.date_sortie.split('T')[0] : '',
        adresse: employee.adresse || '',
        numero_securite_sociale: employee.numero_securite_sociale || '',
        genre: employee.genre || '',
        profile_picture: employee.profile_picture || '',
        site_id: employee.site_id || '',
        manager_id: employee.manager_id || '',
        phone: employee.phone || '',
        email: employee.email || '',
        situation_familiale: employee.situation_familiale || '',
      });
      setIsEditMode(true);
      setSelectedEmployee(employee);
    } else {
      setSelectedEmployee(null);
      setFormData({
        first_name: '',
        last_name: '',
        profession_id: '',
        cin: '',
        date_naissance: '',
        lieu_naissance: '',
        date_embauche: '',
        poste: '',
        date_sortie: '',
        adresse: '',
        numero_securite_sociale: '',
        genre: '',
        profile_picture: '',
        site_id: '',
        manager_id: '',
        phone: '',
        email: '',
        situation_familiale: '',
      });
      setIsEditMode(false);
    }
    setSelectedFile(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // ===== Custom fields: metadata management (admin) =====
  const openMetadataDialog = async () => {
    try {
      const res = await getCustomFieldsMetadata();
      setMetadata(res.data || {});
      setMetaDraft(res.data || {});
      setMetaOpen(true);
    } catch (e) {
      console.error('Erreur chargement metadata:', e);
    }
  };

  const closeMetadataDialog = () => setMetaOpen(false);

  // Helper to generate a safe key from label and ensure uniqueness
  const slugify = (str) =>
    (str || '')
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // remove diacritics
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const uniqueKey = (base, existing) => {
    let k = base || 'field';
    let candidate = k;
    let i = 1;
    while (existing[candidate]) {
      candidate = `${k}_${i++}`;
    }
    return candidate;
  };

  const addMetaField = () => {
    const label = (newMetaLabel || '').trim();
    if (!label) {
      setSnack({ open: true, message: 'Veuillez saisir un label', severity: 'warning' });
      return;
    }
    const base = slugify(label);
    const key = uniqueKey(base, metaDraft);
    setMetaDraft({
      ...metaDraft,
      [key]: { type: newMetaType, editableBy: newMetaEditableBy, label: label || key }
    });
    setNewMetaLabel('');
    setNewMetaType('text');
    setNewMetaEditableBy('admin');
  };

  const updateMetaField = (name, key, value) => {
    setMetaDraft({
      ...metaDraft,
      [name]: { ...(metaDraft[name] || {}), [key]: value }
    });
  };

  const removeMetaField = (name) => {
    const copy = { ...metaDraft };
    delete copy[name];
    setMetaDraft(copy);
  };

  const saveMetadata = async () => {
    try {
      setMetaSaving(true);
      await updateCustomFieldsMetadata(metaDraft);
      setMetadata(metaDraft);
      setMetaOpen(false);
      setSnack({ open: true, message: 'Champs personnalisés sauvegardés', severity: 'success' });
    } catch (e) {
      console.error('Erreur sauvegarde metadata:', e);
      setSnack({ open: true, message: "Erreur lors de l'enregistrement des champs", severity: 'error' });
    } finally {
      setMetaSaving(false);
    }
  };

  // ===== Custom fields: per-employee editor =====
  const openEmployeeFields = async (employee) => {
    try {
      setFieldsEmployee(employee);
      const [metaRes, fieldsRes] = await Promise.all([
        getCustomFieldsMetadata(),
        getEmployeeCustomFields(employee.id),
      ]);
      const meta = metaRes.data || {};
      const vals = fieldsRes.data || {};
      const initial = {};
      Object.keys(meta).forEach((k) => {
        initial[k] = vals[k]?.value ?? '';
      });
      setMetadata(meta);
      setFieldsDraft(initial);
      setFieldsOpen(true);
    } catch (e) {
      console.error('Erreur chargement champs employé:', e);
    }
  };

  const closeEmployeeFields = () => {
    setFieldsOpen(false);
    setFieldsEmployee(null);
    setFieldsDraft({});
  };

  const handleFieldChange = (name, value) => {
    setFieldsDraft({ ...fieldsDraft, [name]: value });
  };

  const handleFieldFileUpload = async (name, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('files', file);
      const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.filePaths;
      setFieldsDraft((prev) => ({ ...prev, [name]: url }));
    } catch (e) {
      console.error('Upload fichier champ personnalisé échoué:', e);
    }
  };

  const saveEmployeeFields = async () => {
    try {
      if (!fieldsEmployee) return;
      setFieldsSaving(true);
      await updateEmployeeCustomFields(fieldsEmployee.id, fieldsDraft);
      setSnack({ open: true, message: 'Champs mis à jour', severity: 'success' });
      closeEmployeeFields();
    } catch (e) {
      console.error('Erreur sauvegarde champs employé:', e);
      setSnack({ open: true, message: "Erreur lors de la sauvegarde des champs", severity: 'error' });
    } finally {
      setFieldsSaving(false);
    }
  };

  const filteredEmployees = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    const fromTime = filterHireFrom ? new Date(filterHireFrom).getTime() : null;
    const toTime = filterHireTo ? new Date(filterHireTo).getTime() : null;

    return (employees || []).filter((e) => {
      // Text search
      const matchesText = !q ||
        (e.first_name || '').toLowerCase().includes(q) ||
        (e.last_name || '').toLowerCase().includes(q) ||
        (e.cin || '').toLowerCase().includes(q) ||
        (e.profession || '').toLowerCase().includes(q) ||
        (e.site || '').toLowerCase().includes(q);

      if (!matchesText) return false;

      // Profession filter
      if (filterProfessionId && String(e.profession_id) !== String(filterProfessionId)) return false;

      // Site filter
      if (filterSiteId && String(e.site_id) !== String(filterSiteId)) return false;

      // Trial status filter
      if (filterTrialStatus !== 'all') {
        const tpe = e.trial_period_end ? new Date(e.trial_period_end) : null;
        const now = new Date();
        const isNone = !tpe;
        const isInTrial = !!tpe && tpe.getTime() >= now.setHours(0,0,0,0);
        const isEnded = !!tpe && !isInTrial;
        if (filterTrialStatus === 'none' && !isNone) return false;
        if (filterTrialStatus === 'in_trial' && !isInTrial) return false;
        if (filterTrialStatus === 'ended' && !isEnded) return false;
      }

      // Hire date range filter
      if (fromTime || toTime) {
        const h = e.hire_date ? new Date(e.hire_date).getTime() : null;
        if (fromTime && (h === null || h < fromTime)) return false;
        if (toTime && (h === null || h > toTime)) return false;
      }

      return true;
    });
  }, [employees, debouncedQuery, filterProfessionId, filterSiteId, filterTrialStatus, filterHireFrom, filterHireTo]);

  const handleRowClick = async (params) => {
    try {
      const response = await api.get(`/employees/${params.row.id}`);
      console.log('Employee details:', response.data); // Debug pour voir la structure
      setSelectedDetails(response.data);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    }
  };

  // Helper pour afficher le username correctement
  const getUsername = (employee) => {
    if (employee.user) {
      if (typeof employee.user === 'object') {
        return employee.user.username || 'Non défini';
      }
      return employee.user || 'Non défini';
    }
    return 'Non défini';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
    
    // Calcul automatique de la date fin de période d'essai
    if (name === 'hire_date' || name === 'periode_essai_mois') {
      const hireDate = name === 'hire_date' ? value : formData.hire_date;
      const trialMonths = name === 'periode_essai_mois' ? value : formData.periode_essai_mois;
      
      if (hireDate && trialMonths && trialMonths > 0) {
        const startDate = new Date(hireDate);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + parseInt(trialMonths));
        
        // Formater la date en YYYY-MM-DD pour le champ input
        const formattedEndDate = endDate.toISOString().split('T')[0];
        
        setFormData(prevData => ({ 
          ...prevData, 
          [name]: value,
          trial_period_end: formattedEndDate 
        }));
      } else if (trialMonths === '' || trialMonths === '0' || !hireDate) {
        // Effacer la date de fin si période d'essai est vide ou 0
        setFormData(prevData => ({ 
          ...prevData, 
          [name]: value,
          trial_period_end: '' 
        }));
      }
    }
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) return '';

    try {
      const formData = new FormData();
      formData.append('files', selectedFile);

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return response.data.filePaths;
    } catch (error) {
      console.error('Error uploading file:', error);
      return '';
    }
  };

  const handleSubmit = async () => {
    if (submitting) {
      return; // Prevent multiple submissions
    }
    
    setSubmitting(true);
    
    try {
      let profilePictureUrl = formData.profile_picture;

      if (selectedFile) {
        profilePictureUrl = await handleUpload();
      }

      const finalData = {
        ...formData,
        profile_picture: profilePictureUrl,
        manager_id: formData.manager_id ? formData.manager_id : null,
      };

      if (isEditMode) {
        await api.put(`/employees/${selectedEmployee.id}`, finalData);
        setEmployees(prevEmployees =>
          prevEmployees.map(e =>
            e.id === selectedEmployee.id
              ? {
                  ...e,
                  ...finalData,
                  profession: (professions.find(p => p.id === finalData.profession_id) || {}).name || '............................',
                  site: (sites.find(s => s.id === finalData.site_id) || {}).name || '—',
                  manager: (() => {
                    const m = (employees || []).find(emp => String(emp.id) === String(finalData.manager_id));
                    return m ? `${m.first_name} ${m.last_name}` : '—';
                  })(),
                }
              : e
          )
        );
      } else {
        const newEmployee = await api.post('/employees', finalData);
        const profession = professions.find(p => p.id === finalData.profession_id);
        const transformedNewEmployee = {
          ...newEmployee.data,
          profession: profession ? profession.name : '............................',
          site: (sites.find(s => s.id === finalData.site_id) || {}).name || '—',
          manager: (() => {
            const m = (employees || []).find(emp => String(emp.id) === String(finalData.manager_id));
            return m ? `${m.first_name} ${m.last_name}` : '—';
          })(),
        };
        setEmployees(prevEmployees => [...prevEmployees, transformedNewEmployee]);
      }

      handleCloseModal();
    } catch (error) {
      console.error('Error saving employee:', error);
      
      // Handle duplicate error
      if (error.response?.status === 409) {
        setSnack({ open: true, message: 'Un employé identique a déjà été créé récemment. Veuillez patienter.', severity: 'error' });
      } else {
        setSnack({ open: true, message: 'Erreur lors de la sauvegarde de l\'employé', severity: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async (id) => {
    if (window.confirm('Archiver cet employé ? Il apparaîtra dans la liste des inactifs.')) {
      try {
        await api.post(`/employees/${id}/archive`);
        setEmployees(prevEmployees => prevEmployees.filter(e => e.id !== id));
        setSnack({ open: true, message: 'Employé archivé', severity: 'success' });
      } catch (error) {
        console.error('Erreur lors de l\'archivage de l\'employé:', error);
        setSnack({ open: true, message: "Échec de l'archivage", severity: 'error' });
      }
    }
  };

  // ===== Leave balance: open/save =====
  const openLeaveBalanceDialog = (employee) => {
    setLbEmployee(employee);
    setLbValue(String(employee.leave_balance ?? ''));
    setLbOpen(true);
  };

  const closeLeaveBalanceDialog = () => {
    setLbOpen(false);
    setLbEmployee(null);
    setLbValue('');
  };

  const saveLeaveBalance = async () => {
    if (!lbEmployee) return;
    try {
      setLbSaving(true);
      const valueNum = Number(lbValue);
      if (Number.isNaN(valueNum)) {
        setSnack({ open: true, message: 'Veuillez saisir un nombre valide', severity: 'warning' });
        setLbSaving(false);
        return;
      }
      await api.put(`/employees/${lbEmployee.id}/leave-balance`, { value: valueNum });
      setEmployees((prev) => prev.map((e) => (e.id === lbEmployee.id ? { ...e, leave_balance: valueNum } : e)));
      setSnack({ open: true, message: 'Solde de congés mis à jour', severity: 'success' });
      closeLeaveBalanceDialog();
    } catch (e) {
      console.error('Maj solde congés échouée:', e);
      setSnack({ open: true, message: "Échec de la mise à jour du solde", severity: 'error' });
    } finally {
      setLbSaving(false);
    }
  };

  const columns = [
    {
      field: 'profile_picture',
      headerName: 'Photo de profil',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {params.row.profile_picture ? (
            <Avatar
              src={params.row.profile_picture}
              alt={`${params.row.first_name} ${params.row.last_name}`}
              sx={{ width: 48, height: 48 }}
            />
          ) : (
            <Avatar sx={{ width: 48, height: 48 }}>
              <PersonIcon />
            </Avatar>
          )}
        </Box>
      ),
    },
    { field: 'first_name', headerName: 'Prénom', width: 150, headerClassName: 'super-app-theme--header' },
    { field: 'last_name', headerName: 'Nom', width: 150, headerClassName: 'super-app-theme--header' },
    { 
      field: 'user.username', 
      headerName: 'Username', 
      width: 150, 
      headerClassName: 'super-app-theme--header',
      valueGetter: (value, row) => {
        const username = row?.user?.username || '—';
        return username;
      },
      editable: true
    },
    { field: 'profession', headerName: 'Service', width: 200, headerClassName: 'super-app-theme--header' },
    { field: 'site', headerName: 'Site', width: 200, headerClassName: 'super-app-theme--header' },
    { field: 'manager', headerName: 'Responsable hiérarchique', width: 220, headerClassName: 'super-app-theme--header' },
    { field: 'situation_familiale', headerName: 'Situation familiale', width: 150, headerClassName: 'super-app-theme--header' },
    { field: 'cin', headerName: 'CIN', width: 150, headerClassName: 'super-app-theme--header' },
    { field: 'date_naissance', headerName: 'Date de naissance', width: 150, headerClassName: 'super-app-theme--header' },
    { field: 'lieu_naissance', headerName: 'Lieu de naissance', width: 180, headerClassName: 'super-app-theme--header' },
    { field: 'date_embauche', headerName: 'Date d\'embauche', width: 150, headerClassName: 'super-app-theme--header' },
    { field: 'poste', headerName: 'Poste', width: 180, headerClassName: 'super-app-theme--header' },
    { field: 'date_sortie', headerName: 'Date de sortie', width: 150, headerClassName: 'super-app-theme--header' },
    { field: 'genre', headerName: 'Genre', width: 100, headerClassName: 'super-app-theme--header' },
    { field: 'numero_securite_sociale', headerName: 'N° Sécurité Sociale', width: 180, headerClassName: 'super-app-theme--header' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 260,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 1 }}>
          {userRole !== 3 && (
            <Tooltip title="Modifier">
              <IconButton onClick={() => handleOpenModal(params.row)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Champs personnalisés">
            <IconButton onClick={() => openEmployeeFields(params.row)}>
              <Chip size="small" label="Champs" />
            </IconButton>
          </Tooltip>
          {userRole === 1 && (
            <Tooltip title="Solde de congés">
              <IconButton onClick={() => openLeaveBalanceDialog(params.row)}>
                <Chip size="small" label="Solde" />
              </IconButton>
            </Tooltip>
          )}
          {userRole !== 3 && (
            <Tooltip title="Archiver">
              <IconButton onClick={() => handleArchive(params.row.id)}>
                <ArchiveIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <h2>Gestion des employés</h2>
      <Stack direction="row" spacing={1} mb={1}>
        {userRole === 1 && (
          <Button variant="contained" color="primary" onClick={() => handleOpenModal()}>
            Ajouter un employé
          </Button>
        )}
        {userRole === 1 && (
          <Button variant="outlined" onClick={openMetadataDialog}>
            Gérer champs personnalisés
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          size="small"
          label="Rechercher"
          placeholder="Nom, CIN, Service..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ width: 320 }}
        />
      </Stack>

      <Accordion expanded={filtersOpen} onChange={() => setFiltersOpen((v) => !v)} sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Filtres</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Service</InputLabel>
              <Select label="Service" value={filterProfessionId} onChange={(e) => setFilterProfessionId(e.target.value)}>
                <MenuItem value="">Toutes</MenuItem>
                {professions.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Site</InputLabel>
              <Select label="Site" value={filterSiteId} onChange={(e) => setFilterSiteId(e.target.value)}>
                <MenuItem value="">Tous</MenuItem>
                {sites.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Période d'essai</InputLabel>
              <Select label="Période d'essai" value={filterTrialStatus} onChange={(e) => setFilterTrialStatus(e.target.value)}>
                <MenuItem value="all">Toutes</MenuItem>
                <MenuItem value="in_trial">En cours</MenuItem>
                <MenuItem value="ended">Terminée</MenuItem>
                <MenuItem value="none">Aucune</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Situation familiale</InputLabel>
              <Select label="Situation familiale" value={filterSituationFamiliale} onChange={(e) => setFilterSituationFamiliale(e.target.value)}>
                <MenuItem value="">Toutes</MenuItem>
                <MenuItem value="Célibataire">Célibataire</MenuItem>
                <MenuItem value="Marié(e)">Marié(e)</MenuItem>
                <MenuItem value="Divorcé(e)">Divorcé(e)</MenuItem>
                <MenuItem value="Veuf(ve)">Veuf(ve)</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type contrat</InputLabel>
              <Select label="Type contrat" value={filterTypeContrat} onChange={(e) => setFilterTypeContrat(e.target.value)}>
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="CDI – Contrat à Durée Indéterminée">CDI</MenuItem>
                <MenuItem value="CDD – Contrat à Durée Déterminée">CDD</MenuItem>
                <MenuItem value="Stage">Stage</MenuItem>
                <MenuItem value="Intérim">Intérim</MenuItem>
                <MenuItem value="Apprentissage">Apprentissage</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" type="date" label="Embauché après" InputLabelProps={{ shrink: true }} value={filterHireFrom} onChange={(e) => setFilterHireFrom(e.target.value)} sx={{ minWidth: 170 }} />
            <TextField size="small" type="date" label="Embauché avant" InputLabelProps={{ shrink: true }} value={filterHireTo} onChange={(e) => setFilterHireTo(e.target.value)} sx={{ minWidth: 170 }} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <Button size="small" startIcon={<FilterAltOffIcon />} onClick={() => { setFilterProfessionId(''); setFilterSiteId(''); setFilterTrialStatus('all'); setFilterHireFrom(''); setFilterHireTo(''); setFilterSituationFamiliale(''); setFilterTypeContrat(''); }}>Réinitialiser</Button>
            <Box sx={{ flexGrow: 1 }} />
            {/* Active filter chips */}
            {filterProfessionId && (
              <Chip label={`Service: ${(professions.find(p => String(p.id) === String(filterProfessionId)) || {}).name || filterProfessionId}`} onDelete={() => setFilterProfessionId('')} size="small" />
            )}
            {filterSiteId && (
              <Chip label={`Site: ${(sites.find(s => String(s.id) === String(filterSiteId)) || {}).name || filterSiteId}`} onDelete={() => setFilterSiteId('')} size="small" />
            )}
            {filterTrialStatus !== 'all' && (
              <Chip label={`Essai: ${filterTrialStatus === 'in_trial' ? 'En cours' : filterTrialStatus === 'ended' ? 'Terminée' : 'Aucune'}`} onDelete={() => setFilterTrialStatus('all')} size="small" />
            )}
            {filterSituationFamiliale && (
              <Chip label={`Famille: ${filterSituationFamiliale}`} onDelete={() => setFilterSituationFamiliale('')} size="small" />
            )}
            {filterTypeContrat && (
              <Chip label={`Contrat: ${filterTypeContrat.substring(0, 3)}`} onDelete={() => setFilterTypeContrat('')} size="small" />
            )}
            {(filterHireFrom || filterHireTo) && (
              <Chip label={`Embauché: ${filterHireFrom || '…'} → ${filterHireTo || '…'}`} onDelete={() => { setFilterHireFrom(''); setFilterHireTo(''); }} size="small" />
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <div style={{ height: 800, marginTop: 10 }}>
      <DataGrid
        rows={filteredEmployees}
        columns={columns}
        pageSize={20}
        pageSizeOptions={[10, 20, 50, 100]}
        autoPageSize={false}
        getRowId={(row) => row.id}
        loading={loadingEmployees}
        disableRowSelectionOnClick
        checkboxSelection={false}
        initialState={{
          sorting: { sortModel: [{ field: 'last_name', sort: 'asc' }] },
          pagination: { paginationModel: { pageSize: 10, page: 0 } },
        }}
        getRowHeight={() => 80}
        onRowClick={handleRowClick}
        sx={{
          '& .super-app-theme--header': {
            backgroundColor: '#1976d2',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
          },
          '& .MuiDataGrid-root': {
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#1976d2',
            fontSize: '14px',
            fontWeight: 600,
            color: 'white',
            borderBottom: '2px solid #1565c0',
          },
          '& .MuiDataGrid-columnHeader': {
            '&:focus': {
              outline: 'none',
            },
            '&:hover': {
              backgroundColor: '#1565c0',
            },
          },
          '& .MuiDataGrid-cell': {
            fontSize: '13px',
            padding: '16px 8px',
            borderBottom: '1px solid #f0f0f0',
            '&:focus': {
              outline: 'none',
            },
          },
          '& .MuiDataGrid-row': {
            '&:nth-of-type(even)': {
              backgroundColor: '#fafafa',
            },
            '&:hover': {
              backgroundColor: '#e3f2fd',
              cursor: 'pointer'
            },
            '&.Mui-selected': {
              backgroundColor: '#e3f2fd',
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-pagination': {
            '& .MuiIconButton-root': {
              borderRadius: '4px',
            },
          },
        }}
      />
      </div>
      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>{isEditMode ? 'Modifier l\'employé' : 'Ajouter un employé'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    autoFocus
                    name="first_name"
                    label="Prénom"
                    type="text"
                    fullWidth
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    error={fieldErrors.first_name}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-error fieldset': {
                          borderColor: 'red',
                          borderWidth: '2px'
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="last_name"
                    label="Nom"
                    type="text"
                    fullWidth
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    error={fieldErrors.last_name}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-error fieldset': {
                          borderColor: 'red',
                          borderWidth: '2px'
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Service</InputLabel>
                    <Select name="profession_id" value={formData.profession_id} onChange={handleChange} required label="Service">
                      {professions.map((profession) => (
                        <MenuItem key={profession.id} value={profession.id}>
                          {profession.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={fieldErrors.site_id}>
                    <InputLabel>Site</InputLabel>
                    <Select name="site_id" value={formData.site_id} onChange={handleChange} label="Site">
                      <MenuItem value="">Aucun</MenuItem>
                      {sites.map((site) => (
                        <MenuItem key={site.id} value={site.id}>
                          {site.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Responsable hiérarchique</InputLabel>
                    <Select
                      name="manager_id"
                      value={formData.manager_id}
                      onChange={handleChange}
                      label="Responsable hiérarchique"
                    >
                      <MenuItem value="">Aucun</MenuItem>
                      {(employees || [])
                        .filter((e) => !isEditMode || e.id !== selectedEmployee?.id)
                        .map((e) => (
                          <MenuItem key={e.id} value={e.id}>
                            {`${e.first_name || ''} ${e.last_name || ''}`.trim()}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField name="phone" label="Téléphone" type="tel" fullWidth value={formData.phone} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField name="email" label="Email" type="email" fullWidth value={formData.email} onChange={handleChange} error={fieldErrors.email} helperText={fieldErrors.email} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={fieldErrors.genre}>
                    <InputLabel>Genre</InputLabel>
                    <Select name="genre" value={formData.genre || ''} onChange={handleChange} label="Genre">
                      <MenuItem value="">Non spécifié</MenuItem>
                      <MenuItem value="Homme">Homme</MenuItem>
                      <MenuItem value="Femme">Femme</MenuItem>
                    </Select>
                    {fieldErrors.genre && <FormHelperText>{fieldErrors.genre}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Situation familiale</InputLabel>
                    <Select name="situation_familiale" value={formData.situation_familiale} onChange={handleChange} label="Situation familiale">
                      <MenuItem value="">Aucune</MenuItem>
                      <MenuItem value="Célibataire">Célibataire</MenuItem>
                      <MenuItem value="Marié(e)">Marié(e)</MenuItem>
                      <MenuItem value="Divorcé(e)">Divorcé(e)</MenuItem>
                      <MenuItem value="Veuf(ve)">Veuf(ve)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField name="email" label="Email" type="email" fullWidth value={formData.email} onChange={handleChange} required
                    error={fieldErrors.email}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-error fieldset': {
                          borderColor: 'red',
                          borderWidth: '2px'
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField name="date_naissance" label="Date de naissance" type="date" fullWidth InputLabelProps={{ shrink: true }} value={formData.date_naissance} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField name="lieu_naissance" label="Lieu de naissance" type="text" fullWidth value={formData.lieu_naissance} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField name="date_embauche" label="Date d'embauche" type="date" fullWidth InputLabelProps={{ shrink: true }} value={formData.date_embauche} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField name="poste" label="Poste" type="text" fullWidth value={formData.poste} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField name="date_sortie" label="Date de sortie" type="date" fullWidth InputLabelProps={{ shrink: true }} value={formData.date_sortie} onChange={handleChange} />
                </Grid>
                <Grid item xs={12}>
                  <TextField name="adresse" label="Adresse" type="text" fullWidth multiline rows={2} value={formData.adresse} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField name="numero_securite_sociale" label="Numéro de Sécurité Sociale" type="text" fullWidth value={formData.numero_securite_sociale} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField name="cin" label="CIN" type="text" fullWidth value={formData.cin} onChange={handleChange} required 
                    error={fieldErrors.cin}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-error fieldset': {
                          borderColor: 'red',
                          borderWidth: '2px'
                        }
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack alignItems="center" spacing={2}>
                <Avatar
                  src={selectedFile ? URL.createObjectURL(selectedFile) : (formData.profile_picture || '')}
                  sx={{ width: 96, height: 96 }}
                >
                  {!selectedFile && !formData.profile_picture && <PersonIcon />}
                </Avatar>
                <Button variant="outlined" component="label">
                  Importer une photo
                  <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                </Button>
                {selectedFile && (
                  <Typography variant="caption" color="textSecondary">{selectedFile.name}</Typography>
                )}
                {uploadProgress > 0 && (
                  <LinearProgress sx={{ width: '100%' }} variant="determinate" value={uploadProgress} />
                )}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={submitting}>Annuler</Button>
          <Button onClick={handleSubmit} color="primary" disabled={submitting}>
            {submitting ? (isEditMode ? 'Enregistrement...' : 'Ajout...') : (isEditMode ? 'Enregistrer' : 'Ajouter')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Metadata management dialog */}
      <Dialog open={metaOpen} onClose={closeMetadataDialog} fullWidth maxWidth="md">
        <DialogTitle>Gestion des champs personnalisés</DialogTitle>
        <DialogContent>
          <Box mb={2} display="grid" gridTemplateColumns="1fr 160px 180px 140px" gap={2}>
            <TextField label="Label" value={newMetaLabel} onChange={(e) => setNewMetaLabel(e.target.value)} />
            <FormControl>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={newMetaType} onChange={(e) => setNewMetaType(e.target.value)}>
                <MenuItem value="text">Texte</MenuItem>
                <MenuItem value="number">Nombre</MenuItem>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="file">Fichier</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Modifiable par</InputLabel>
              <Select label="Modifiable par" value={newMetaEditableBy} onChange={(e) => setNewMetaEditableBy(e.target.value)}>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="user">Utilisateur</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={addMetaField}>Ajouter</Button>
          </Box>
          {Object.keys(metaDraft || {}).length === 0 && (
            <Typography color="textSecondary">Aucun champ défini</Typography>
          )}
          {Object.entries(metaDraft || {}).map(([name, meta]) => (
            <Box key={name} p={1} mb={1} border={1} borderColor="#eee" borderRadius={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">{name}</Typography>
                <Button color="error" onClick={() => removeMetaField(name)}>Supprimer</Button>
              </Box>
              <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mt={1}>
                <TextField label="Label" value={meta.label || ''} onChange={(e) => updateMetaField(name, 'label', e.target.value)} />
                <FormControl>
                  <InputLabel>Type</InputLabel>
                  <Select label="Type" value={meta.type || 'text'} onChange={(e) => updateMetaField(name, 'type', e.target.value)}>
                    <MenuItem value="text">Texte</MenuItem>
                    <MenuItem value="number">Nombre</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="file">Fichier</MenuItem>
                  </Select>
                </FormControl>
                <FormControl>
                  <InputLabel>Modifiable par</InputLabel>
                  <Select label="Modifiable par" value={meta.editableBy || 'admin'} onChange={(e) => updateMetaField(name, 'editableBy', e.target.value)}>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="user">Utilisateur</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMetadataDialog}>Annuler</Button>
          <Button variant="contained" onClick={saveMetadata} disabled={metaSaving}>
            {metaSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee fields dialog */}
      <Dialog open={fieldsOpen} onClose={closeEmployeeFields} fullWidth maxWidth="md">
        <DialogTitle>Champs personnalisés — {fieldsEmployee?.first_name} {fieldsEmployee?.last_name}</DialogTitle>
        <DialogContent>
          {/* View-only for comptable */}
          {(() => { /* compute editability */ return null; })()}
          {Object.keys(metadata || {}).length === 0 ? (
            <Typography color="textSecondary">Aucun champ configuré</Typography>
          ) : (
            Object.entries(metadata).map(([name, meta]) => (
              <Box key={name} mb={2}>
                <Typography variant="caption">{meta.label || name}</Typography>
                {meta.type === 'date' ? (
                  <TextField fullWidth type="date" value={fieldsDraft[name] || ''} onChange={(e) => handleFieldChange(name, e.target.value)} InputLabelProps={{ shrink: true }} disabled={userRole === 3} />
                ) : meta.type === 'number' ? (
                  <TextField fullWidth type="number" value={fieldsDraft[name] || ''} onChange={(e) => handleFieldChange(name, e.target.value)} disabled={userRole === 3} />
                ) : meta.type === 'file' ? (
                  <Box>
                    {fieldsDraft[name] && (
                      <Box mb={1}>
                        <Link href={fieldsDraft[name]} target="_blank" rel="noopener">Fichier actuel</Link>
                      </Box>
                    )}
                    {userRole !== 3 && (
                      <input type="file" onChange={(e) => handleFieldFileUpload(name, e.target.files?.[0])} />
                    )}
                  </Box>
                ) : (
                  <TextField fullWidth value={fieldsDraft[name] || ''} onChange={(e) => handleFieldChange(name, e.target.value)} disabled={userRole === 3} />
                )}
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEmployeeFields}>Annuler</Button>
          {userRole !== 3 && (
            <Button variant="contained" onClick={saveEmployeeFields} disabled={fieldsSaving}>
              {fieldsSaving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Leave balance dialog */}
      <Dialog open={lbOpen} onClose={closeLeaveBalanceDialog} fullWidth maxWidth="xs">
        <DialogTitle>Solde de congés — {lbEmployee?.first_name} {lbEmployee?.last_name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Solde (jours)"
            type="number"
            fullWidth
            value={lbValue}
            onChange={(e) => setLbValue(e.target.value)}
            inputProps={{ step: '0.5' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLeaveBalanceDialog}>Annuler</Button>
          <Button variant="contained" onClick={saveLeaveBalance} disabled={lbSaving}>
            {lbSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>

      {/* Details Modal */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Détails de l'employé</DialogTitle>
        <DialogContent>
          {selectedDetails && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
              <div><strong>Prénom:</strong> {selectedDetails.first_name || '—'}</div>
              <div><strong>Nom:</strong> {selectedDetails.last_name || '—'}</div>
              <div><strong>Service:</strong> {selectedDetails.profession?.name || selectedDetails.profession || '—'}</div>
              <div><strong>Responsable hiérarchique:</strong> {selectedDetails.manager ? `${selectedDetails.manager.first_name || ''} ${selectedDetails.manager.last_name || ''}`.trim() || '—' : '—'}</div>
              <div><strong>Situation familiale:</strong> {selectedDetails.situation_familiale || '—'}</div>
              <div><strong>CIN:</strong> {selectedDetails.cin || '—'}</div>
              <div><strong>Date de naissance:</strong> {selectedDetails.date_naissance ? new Date(selectedDetails.date_naissance).toLocaleDateString() : '—'}</div>
              <div><strong>Lieu de naissance:</strong> {selectedDetails.lieu_naissance || '—'}</div>
              <div><strong>Date d'embauche:</strong> {selectedDetails.date_embauche ? new Date(selectedDetails.date_embauche).toLocaleDateString() : '—'}</div>
              <div><strong>Poste:</strong> {selectedDetails.poste || '—'}</div>
              <div><strong>Date de sortie:</strong> {selectedDetails.date_sortie ? new Date(selectedDetails.date_sortie).toLocaleDateString() : '—'}</div>
              <div><strong>Adresse:</strong> {selectedDetails.adresse || '—'}</div>
              <div><strong>N° Sécurité Sociale:</strong> {selectedDetails.numero_securite_sociale || '—'}</div>
              <div><strong>Genre:</strong> {selectedDetails.genre || '—'}</div>
              <div><strong>Téléphone:</strong> {selectedDetails.phone || '—'}</div>
              <div><strong>Email:</strong> {selectedDetails.email || '—'}</div>
              <div><strong>Solde de congés:</strong> {selectedDetails.leave_balance !== undefined && selectedDetails.leave_balance !== null ? selectedDetails.leave_balance : '—'}</div>
             </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;